import { getCustomerDisplayName } from '#lib/helper/customer.js';
import { generateUUID } from '#lib/helper/uuid.js';
import { now } from '#lib/stores/time.js';
import {
	PaymentMethod,
	PaymentStatus,
	TransactionOperation,
	type Booking,
	type Customer,
	type Giftcard,
	type GiftcardStats,
	type Transaction
} from '#lib/types/generated/index.js';
import type { DateTime } from 'luxon';
import { get } from 'svelte/store';

interface OptimisticGiftcardTransactionParams {
	giftcard: Giftcard;
	amount: number;
	bookingId?: string;
	customer?: Customer | null;
	paymentId?: string;
	createdAt?: DateTime;
}

export function createOptimisticGiftcardConsumptionTransaction(
	params: OptimisticGiftcardTransactionParams
): Transaction {
	const { giftcard, amount, bookingId, customer, paymentId, createdAt } = params;
	const effectiveDate = createdAt ?? get(now);
	const targetCustomer = customer ?? giftcard.customer;

	return {
		id: paymentId ?? generateUUID(),
		createdAt: effectiveDate,
		effectiveDate,
		operation: TransactionOperation.GiftcardConsumption,
		status: PaymentStatus.Success,
		method: PaymentMethod.Giftcard,
		amount: 0,
		amountRefunded: 0,
		fee: 0,
		net: 0,
		value: -Math.abs(amount),
		isSplitPayment: false,
		splitFee: 0,
		ownerSplitFee: 0,
		posMethod: null,
		parentPaymentId: null,
		giftcardCode: giftcard.code,
		customerPackageId: null,
		customerPackageName: null,
		bookingId: bookingId ?? null,
		bookingDate: null,
		bookingStartTime: null,
		bookingStatus: null,
		bookingPax: null,
		bookingPrice: null,
		bookingOffers: null,
		customerId: targetCustomer?.id ?? null,
		customerName: getCustomerDisplayName(targetCustomer),
		customerFirstname: targetCustomer?.firstName ?? null,
		customerLastname: targetCustomer?.lastName ?? null,
		customerEmail: targetCustomer?.email ?? null,
		customerPhone: targetCustomer?.phone ?? null,
		giftcardOrderId: giftcard.giftcardOrderId ? String(giftcard.giftcardOrderId) : null,
		giftcardQuantity: null,
		giftcardName: giftcard.label ?? null,
		customerPackageOrderId: null,
		billingInvoiceId: null
	};
}

export function createOptimisticGiftcardRecreditTransaction(
	params: OptimisticGiftcardTransactionParams
): Transaction {
	const { giftcard, amount, bookingId, customer, paymentId, createdAt } = params;
	const effectiveDate = createdAt ?? get(now);
	const targetCustomer = customer ?? giftcard.customer;

	return {
		id: paymentId ?? generateUUID(),
		createdAt: effectiveDate,
		effectiveDate,
		operation: TransactionOperation.GiftcardRecredit,
		status: PaymentStatus.Success,
		method: PaymentMethod.Giftcard,
		amount: 0,
		amountRefunded: 0,
		fee: 0,
		net: 0,
		value: Math.abs(amount),
		isSplitPayment: false,
		splitFee: 0,
		ownerSplitFee: 0,
		posMethod: null,
		parentPaymentId: null,
		giftcardCode: giftcard.code,
		customerPackageId: null,
		customerPackageName: null,
		bookingId: bookingId ?? null,
		bookingDate: null,
		bookingStartTime: null,
		bookingStatus: null,
		bookingPax: null,
		bookingPrice: null,
		bookingOffers: null,
		customerId: targetCustomer?.id ?? null,
		customerName: getCustomerDisplayName(targetCustomer),
		customerFirstname: targetCustomer?.firstName ?? null,
		customerLastname: targetCustomer?.lastName ?? null,
		customerEmail: targetCustomer?.email ?? null,
		customerPhone: targetCustomer?.phone ?? null,
		giftcardOrderId: giftcard.giftcardOrderId ? String(giftcard.giftcardOrderId) : null,
		giftcardQuantity: null,
		giftcardName: giftcard.label ?? null,
		customerPackageOrderId: null,
		billingInvoiceId: null
	};
}

export function applyOptimisticGiftcardConsumption(giftcard: Giftcard, amount: number): Giftcard {
	return {
		...giftcard,
		remainingAmount: Math.max(0, giftcard.remainingAmount - amount)
	};
}

export function applyOptimisticGiftcardRecredit(giftcard: Giftcard, amount: number): Giftcard {
	return {
		...giftcard,
		remainingAmount: giftcard.remainingAmount + amount
	};
}

/**
 * Returns true if the giftcard is expired relative to the given reference time.
 */
export function isGiftcardExpired(giftcard: Pick<Giftcard, 'expiresAt'>, at?: DateTime): boolean {
	const current = at ?? get(now);
	return giftcard.expiresAt < current;
}

/**
 * Returns true if the giftcard has been fully consumed (remaining balance is zero or less).
 */
export function isGiftcardConsumed(giftcard: Pick<Giftcard, 'remainingAmount'>): boolean {
	return giftcard.remainingAmount <= 0;
}

/**
 * Returns true if the giftcard is past (either expired or fully consumed).
 */
export function isGiftcardPast(
	giftcard: Pick<Giftcard, 'expiresAt' | 'remainingAmount'>,
	at?: DateTime
): boolean {
	return isGiftcardExpired(giftcard, at) || isGiftcardConsumed(giftcard);
}

export function applyOptimisticGiftcardStatsConsumption(
	stats: GiftcardStats,
	amount: number
): GiftcardStats {
	return {
		...stats,
		remaining: Math.max(0, stats.remaining - amount)
	};
}

export function applyOptimisticGiftcardStatsRecredit(
	stats: GiftcardStats,
	amount: number
): GiftcardStats {
	return {
		...stats,
		remaining: stats.remaining + amount
	};
}

const recentLocalGiftcardMutations = new Map<string, number>();

export function markGiftcardLocallyMutated(giftcardId: string): void {
	recentLocalGiftcardMutations.set(giftcardId, Date.now());
	setTimeout(() => {
		recentLocalGiftcardMutations.delete(giftcardId);
	}, 5000);
}

export function isGiftcardRecentlyMutated(giftcardId: string, windowMs: number = 4000): boolean {
	const lastTime = recentLocalGiftcardMutations.get(giftcardId);
	if (!lastTime) return false;
	return Date.now() - lastTime < windowMs;
}

export function clearRecentGiftcardMutations(): void {
	recentLocalGiftcardMutations.clear();
}

/**
 * Returns the net amount consumed by a booking for a specific giftcard.
 */
export function getGiftcardBookingConsumedAmount(
	giftcard: Pick<Giftcard, 'id' | 'code'>,
	booking?: Booking | null
): number {
	if (!booking?.payments) return 0;
	const cleanCode = giftcard.code.replace(/^#/, '').trim().toLowerCase();
	const cleanId = giftcard.id.trim().toLowerCase();

	return booking.payments
		.filter((p) => {
			if (p.method !== PaymentMethod.Giftcard || p.status !== PaymentStatus.Success) {
				return false;
			}
			const obj = p.objectId?.replace(/^#/, '').trim().toLowerCase();
			return Boolean(obj && (obj === cleanCode || obj === cleanId));
		})
		.reduce((sum, p) => {
			const val = Math.abs(p.value || p.bookingAmount || p.amount || 0);
			const isRefund = p.bookingAmount < 0 || p.amount < 0 || Boolean(p.parentPaymentId);
			return isRefund ? sum - val : sum + val;
		}, 0);
}
